import React, { useCallback } from "react";

import { useTranslation } from "react-i18next";

import { Measurement } from "../../../../../../../../models/measurement";
import { ShipmentContainerInfo } from "../../../../../../../../models/shipments";
import { DEFAULT_MAX_LENGTH, handleGetConvertValue } from "../../../../../../../../util/converts";
import { ShipmentContainerInfoDetails } from "../../../../../../../../util/enums";
import { DecimalFormatType } from "../../../../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../../../../util/getMeasurement";
import { FieldViewProps } from "../../../../../../../Form/FieldView";
import { SectionView } from "../../../../../../../Generic/SideView/SectionView";
import getShipmentOptions from "../../../../../../hooks/getShipmentOptions";

interface ContainerViewProps {
  containerInfo: ShipmentContainerInfo;
}

export const ContainerInfoView = ({ containerInfo }: ContainerViewProps) => {
  const { t: translate } = useTranslation();
  const { containerTypeOptions } = getShipmentOptions();

  const baseViewFieldProps = (key: keyof Omit<ShipmentContainerInfo, "shipment">) => {
    const props: FieldViewProps = {
      label: translate(key),
      value: containerInfo?.[key],
    };

    return props;
  };
  const measurementWeight = getWeightMeasurement();
  const measurementGeneral = getMeasurement();
  const decimalPlacesCustom = DecimalFormatType.MAERSK;

  const getMaxLength = useCallback(
    (measurement: Measurement) => {
      return decimalPlacesCustom || measurement?.decimalPlaces || DEFAULT_MAX_LENGTH;
    },
    [decimalPlacesCustom, measurementWeight, measurementGeneral]
  );

  const formatContainerValue = (value: number, measurement: Measurement) => {
    return String(
      handleGetConvertValue(Number(value), measurement?.code, getMaxLength(measurement))
    );
  };

  const containerSizeOption = containerTypeOptions.find(
    (type) => type.value === containerInfo?.[ShipmentContainerInfoDetails.SIZE]
  );

  const fields = [
    [
      {
        ...baseViewFieldProps(ShipmentContainerInfoDetails.CONTAINER),
        label: `${translate("container")} #`,
      },
      {
        value: containerSizeOption?.label,
        label: translate("containerSize"),
      },
      {
        ...baseViewFieldProps(ShipmentContainerInfoDetails.SEAL),
        label: `${translate("seal")} #`,
      },
      {
        ...baseViewFieldProps(ShipmentContainerInfoDetails.TARE_WEIGHT),
        value: formatContainerValue(containerInfo.tareWeight ?? 0, measurementWeight),
      },
    ],
    [
      {
        ...baseViewFieldProps(ShipmentContainerInfoDetails.NET_WEIGHT),
        value: formatContainerValue(containerInfo.netWeight ?? 0, measurementWeight),
      },
      {
        ...baseViewFieldProps(ShipmentContainerInfoDetails.GROSS_WEIGHT),
        value: formatContainerValue(Number(containerInfo.grossWeight) ?? 0, measurementWeight),
      },
      {
        ...baseViewFieldProps(ShipmentContainerInfoDetails.GROSS_VOLUME),
        value: formatContainerValue(containerInfo.grossVolume ?? 0, measurementGeneral),
      },
      {
        ...baseViewFieldProps(ShipmentContainerInfoDetails.VOLUMETRIC_WEIGHT),
        value: formatContainerValue(containerInfo.volumetricWeight ?? 0, measurementWeight),
      },
    ],
  ];

  return <SectionView fields={fields} />;
};
